// src/client.ts
import {
  ContractFunctionExecutionError,
  encodeFunctionData
} from "viem";

// src/errors/index.ts
var DepositRevert = class extends Error {
  constructor(message) {
    super(message);
    this.name = "Deposit Reverted";
  }
};
var HttpError = class extends Error {
  url;
  status;
  constructor(params, opts) {
    super(params.message, opts);
    this.name = params.name ?? "HttpError";
    this.url = params.url;
    this.status = params.status;
  }
};
var AcrossApiError = class extends HttpError {
  constructor(params, opts) {
    super(
      {
        ...params,
        name: params.name ?? "AcrossApiError"
      },
      opts
    );
  }
};
var AcrossApiSimulationError = class extends AcrossApiError {
  transaction;
  constructor(params, opts) {
    super(
      {
        ...params,
        name: "AcrossApiSimulationError",
        status: 400,
        code: "SIMULATION_ERROR"
      },
      opts
    );
    this.transaction = params.transaction;
  }
};
var SimulationError = class extends Error {
  simulationId;
  simulationUrl;
  constructor(params, opts) {
    super(params.message, opts);
    this.name = "SimulationError";
    this.simulationId = params.simulationId;
    this.simulationUrl = params.simulationUrl;
  }
};
var IndexerError = class extends Error {
  url;
  constructor(url, message, error) {
    super(message);
    this.name = error ?? "Indexer Error";
    this.url = url;
  }
};
var ConfigError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "Config Error";
  }
};
var NoDepositLogError = class extends Error {
  constructor(txHash, chainId) {
    super(`Unable to parse depositV3 log for tx ${txHash} on chain ${chainId}`);
    this.name = "Deposit Log Not Found";
  }
};
var NoFillLogError = class extends Error {
  constructor(depositId, depositTxHash, chainId) {
    super(
      `Unable to find fill log on chain ${chainId} for deposit id #${depositId} with depositTxHash ${depositTxHash}`
    );
    this.name = "Fill Log Not Found";
  }
};

// src/utils/fetch.ts
function buildSearchParams(params) {
  const searchParams = new URLSearchParams();
  for (const key in params) {
    const value = params[key];
    if (!value) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((val) => searchParams.append(key, String(val)));
    } else {
      searchParams.append(key, String(value));
    }
  }
  return searchParams.toString();
}
function isOk(res) {
  if (res.status >= 200 && res.status < 300 || res.status === 304) {
    return true;
  }
  return false;
}
function makeFetcher(name, apiErrorHandler) {
  return async (apiUrl, params, logger) => {
    const searchParams = buildSearchParams(
      params
    );
    const url = `${apiUrl}?${searchParams}`;
    logger == null ? void 0 : logger.debug(`Fetching ${name}...`, url);
    const res = await fetch(url);
    let data;
    try {
      data = await res.json();
    } catch (e) {
      data = await res.text();
    }
    if (isOk(res)) {
      logger == null ? void 0 : logger.debug("OK response", data);
      return data;
    }
    logger == null ? void 0 : logger.debug("Error response", {
      status: res.status
    });
    if (apiErrorHandler) {
      apiErrorHandler(res, data, url);
    }
    throw new HttpError({
      status: res.status,
      message: typeof data === "string" ? data : JSON.stringify(data),
      url
    });
  };
}
var fetchAcrossApi = makeFetcher("Across API", (res, data, url) => {
  if (typeof data === "object" && data !== null && "type" in data && data.type === "AcrossApiError") {
    const acrossApiError = data;
    if (acrossApiError.code === "SIMULATION_ERROR") {
      throw new AcrossApiSimulationError({
        message: acrossApiError.message,
        url,
        transaction: acrossApiError.transaction
      });
    }
    throw new AcrossApiError({
      status: res.status,
      message: acrossApiError.message,
      url,
      code: acrossApiError.code
    });
  }
});
var fetchIndexerApi = makeFetcher(
  "Indexer API",
  async (res, data, url) => {
    if (typeof data === "object" && data !== null && "error" in data) {
      throw new IndexerError(url, data == null ? void 0 : data.message, data == null ? void 0 : data.error);
    }
  }
);

// src/utils/logger.ts
var LogLevels = {
  DEBUG: 3,
  INFO: 2,
  WARN: 1,
  ERROR: 0
};
var DefaultLogger = class {
  logLevel;
  secondaryLabel = "@across-protocol/app-sdk";
  logPrefix = ">";
  constructor(logLevel) {
    this.logLevel = logLevel;
  }
  createLogLevelLabel = (logLevel) => {
    const label = `${setColor(colorMap[logLevel].primary)} ${logLevel} ${resetControl}${setColor(colorMap[logLevel].secondary)} ${this.secondaryLabel}${resetControl}`;
    const prefix = `${setColor(colorMap[logLevel].secondary)}${this.logPrefix}${resetControl}`;
    return {
      label,
      prefix
    };
  };
  /**
   * Description - creates an indentation group for each call to `logger.[logLevel]()`
   */
  log(logLevel, ...data) {
    const { label, prefix } = this.createLogLevelLabel(logLevel);
    console.log(`${label}
`);
    data.forEach((item) => {
      console.log(prefix, item, "\n");
    });
  }
  debug(...data) {
    if (LogLevels["DEBUG"] <= LogLevels[this.logLevel]) {
      this.log("DEBUG", ...data);
    }
  }
  info(...data) {
    if (LogLevels["INFO"] <= LogLevels[this.logLevel]) {
      this.log("INFO", ...data);
    }
  }
  warn(...data) {
    if (LogLevels["WARN"] <= LogLevels[this.logLevel]) {
      this.log("WARN", ...data);
    }
  }
  error(...data) {
    if (LogLevels["ERROR"] <= LogLevels[this.logLevel]) {
      this.log("ERROR", ...data);
    }
  }
};
var COLORS = {
  Black: 30,
  Red: 31,
  Green: 32,
  Yellow: 33,
  Blue: 34,
  Magenta: 35,
  Cyan: 36,
  White: 37,
  BgBlack: 40,
  BgRed: 41,
  BgGreen: 42,
  BgYellow: 43,
  BgBlue: 44,
  BgMagenta: 45,
  BgCyan: 46,
  BgWhite: 47
};
var resetControl = "\x1B[0m";
var setColor = (color) => `\x1B[${color}m`;
var colorMap = {
  DEBUG: {
    primary: COLORS.BgBlue,
    secondary: COLORS.Blue
  },
  INFO: {
    primary: COLORS.BgCyan,
    secondary: COLORS.Cyan
  },
  WARN: {
    primary: COLORS.BgYellow,
    secondary: COLORS.Yellow
  },
  ERROR: {
    primary: COLORS.BgRed,
    secondary: COLORS.Red
  }
};

// src/utils/multicallHandler.ts
import { encodeAbiParameters, parseAbiParameters } from "viem";
function getMultiCallHandlerAddress(chainId) {
  const defaultAddress = "0x924a9f036260DdD5808007E1AA95f08eD08aA569";
  switch (chainId) {
    case 324:
      return "0x863859ef502F0Ee9676626ED5B418037252eFeb2";
    case 59144:
      return "0x1015c58894961F4F7Dd7D68ba033e28Ed3ee1cDB";
    default:
      return defaultAddress;
  }
}
function buildMulticallHandlerMessage(params) {
  const instructionsAbiParams = parseAbiParameters(
    "((address target, bytes callData, uint256 value)[], address fallbackRecipient)"
  );
  return encodeAbiParameters(instructionsAbiParams, [
    [
      params.actions.map(({ target, callData, value }) => ({
        target,
        callData,
        value: BigInt(value)
      })),
      params.fallbackRecipient
    ]
  ]);
}
var MulticallHandlerAbi = [
  {
    inputs: [
      { internalType: "uint256", name: "index", type: "uint256" },
      {
        components: [
          { internalType: "address", name: "target", type: "address" },
          { internalType: "bytes", name: "callData", type: "bytes" },
          { internalType: "uint256", name: "value", type: "uint256" }
        ],
        internalType: "struct MulticallHandler.Call[]",
        name: "calls",
        type: "tuple[]"
      }
    ],
    name: "CallReverted",
    type: "error"
  },
  {
    inputs: [
      { internalType: "uint256", name: "index", type: "uint256" },
      {
        components: [
          { internalType: "address", name: "target", type: "address" },
          { internalType: "bytes", name: "callData", type: "bytes" },
          { internalType: "uint256", name: "value", type: "uint256" }
        ],
        internalType: "struct MulticallHandler.Call[]",
        name: "calls",
        type: "tuple[]"
      }
    ],
    name: "InvalidCall",
    type: "error"
  },
  {
    anonymous: false,
    inputs: [
      {
        components: [
          { internalType: "address", name: "target", type: "address" },
          { internalType: "bytes", name: "callData", type: "bytes" },
          { internalType: "uint256", name: "value", type: "uint256" }
        ],
        indexed: false,
        internalType: "struct MulticallHandler.Call[]",
        name: "calls",
        type: "tuple[]"
      },
      {
        indexed: true,
        internalType: "address",
        name: "fallbackRecipient",
        type: "address"
      }
    ],
    name: "CallsFailed",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: "address",
        name: "recipient",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "token",
        type: "address"
      },
      {
        indexed: true,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      }
    ],
    name: "DrainedTokens",
    type: "event"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "target", type: "address" },
          { internalType: "bytes", name: "callData", type: "bytes" },
          { internalType: "uint256", name: "value", type: "uint256" }
        ],
        internalType: "struct MulticallHandler.Call[]",
        name: "calls",
        type: "tuple[]"
      }
    ],
    name: "attemptCalls",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "token", type: "address" },
      { internalType: "address payable", name: "destination", type: "address" }
    ],
    name: "drainLeftoverTokens",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "token", type: "address" },
      { internalType: "uint256", name: "", type: "uint256" },
      { internalType: "address", name: "", type: "address" },
      { internalType: "bytes", name: "message", type: "bytes" }
    ],
    name: "handleV3AcrossMessage",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  { stateMutability: "payable", type: "receive" }
];

// src/utils/timestamp.ts
function getCurrentTimeSeconds() {
  return Math.floor(Date.now() / 1e3);
}

// src/utils/hex.ts
import { concat, isHex } from "viem";
var DOMAIN_CALLDATA_DELIMITER = "0x1dc0de";
function tagIntegratorId(integratorId, txData) {
  assertValidIntegratorId(integratorId);
  return concat([txData, DOMAIN_CALLDATA_DELIMITER, integratorId]);
}
function getIntegratorDataSuffix(integratorId) {
  assertValidIntegratorId(integratorId);
  return concat([DOMAIN_CALLDATA_DELIMITER, integratorId]);
}
function isValidIntegratorId(integratorId) {
  return isHex(integratorId) && // "0x" + 2 bytes = 6 hex characters
  integratorId.length === 6;
}
function assertValidIntegratorId(integratorId) {
  if (!isValidIntegratorId(integratorId)) {
    throw new Error(
      `Invalid integrator ID: ${integratorId}. Needs to be 2 bytes hex string.`
    );
  }
  return true;
}

// src/utils/configurePublicClients.ts
import { createPublicClient, http, webSocket } from "viem";
function configurePublicClients(chains, pollingInterval, rpcUrls, transports) {
  return new Map(
    chains.map((chain) => {
      const rpcUrl = rpcUrls == null ? void 0 : rpcUrls[chain.id];
      const customTransport = transports == null ? void 0 : transports[chain.id];
      const transport = customTransport ?? ((rpcUrl == null ? void 0 : rpcUrl.startsWith("wss")) ? webSocket(rpcUrl) : http(rpcUrl));
      return [
        chain.id,
        createPublicClient({
          chain,
          pollingInterval,
          key: chain.id.toString(),
          transport,
          batch: {
            multicall: true
          }
        })
      ];
    })
  );
}

// src/constants/index.ts
var MAINNET_API_URL = "https://app.across.to/api";
var TESTNET_API_URL = "https://testnet.across.to/api";
var MAINNET_INDEXER_API = "https://public.api.across.to";
var TESTNET_INDEXER_API = "https://dev.public.api.across.to";

// src/utils/getSupportedChains.ts
async function getSupportedChains({
  logger,
  apiUrl = MAINNET_API_URL,
  ...params
}) {
  const data = await fetchAcrossApi(
    `${apiUrl}/chains`,
    params,
    logger
  );
  return data;
}

// src/utils/tenderly.ts
async function simulateTxOnTenderly(params) {
  const simUrl = `https://api.tenderly.co/api/v1/account/${params.accountSlug}/project/${params.projectSlug}/simulate`;
  const simRes = await fetch(simUrl, {
    method: "POST",
    headers: {
      "X-Access-Key": params.accessKey
    },
    body: JSON.stringify({
      network_id: params.networkId,
      from: params.from,
      to: params.to,
      input: params.data,
      gas: params.gas ? Number(params.gas) : void 0,
      block_number: params.blockNumber,
      save: true
    })
  });
  if (!isOk(simRes)) {
    throw new HttpError({
      status: simRes.status,
      url: simUrl,
      message: `Failed to simulate tx on Tenderly - ${simRes.status} ${simRes.statusText} - ${await simRes.text()}`
    });
  }
  const simData = await simRes.json();
  const simulationId = simData.simulation.id;
  if (params.enableShare) {
    const enableShareUrl = `https://api.tenderly.co/api/v1/account/${params.accountSlug}/project/${params.projectSlug}/simulations/${simulationId}/share`;
    const enableShareRes = await fetch(enableShareUrl, {
      method: "POST",
      headers: {
        "X-Access-Key": params.accessKey
      }
    });
    if (!isOk(enableShareRes)) {
      throw new HttpError({
        status: enableShareRes.status,
        url: enableShareUrl,
        message: `Failed to enable sharing Tenderly simulation - ${enableShareRes.status} ${enableShareRes.statusText} - ${await enableShareRes.text()}`
      });
    }
  }
  const url = params.enableShare ? `https://www.tdly.co/shared/simulation/${simulationId}` : `https://dashboard.tenderly.co/${params.accountSlug}/${params.projectSlug}/simulator/${simulationId}`;
  return {
    simulationId,
    simulationUrl: url
  };
}

// src/actions/getSuggestedFees.ts
async function getSuggestedFees({
  apiUrl = MAINNET_API_URL,
  logger,
  ...params
}) {
  const data = await fetchAcrossApi(
    `${apiUrl}/suggested-fees`,
    {
      depositMethod: "depositExclusive",
      ...params
    },
    logger
  );
  return {
    estimatedFillTimeSec: data.estimatedFillTimeSec,
    outputAmount: BigInt(params.amount) - BigInt(data.totalRelayFee.total),
    timestamp: Number(data.timestamp),
    isAmountTooLow: data.isAmountTooLow,
    quoteBlock: Number(data.quoteBlock),
    exclusiveRelayer: data.exclusiveRelayer,
    exclusivityDeadline: data.exclusivityDeadline,
    spokePoolAddress: data.spokePoolAddress,
    destinationSpokePoolAddress: data.destinationSpokePoolAddress,
    totalRelayFee: {
      pct: BigInt(data.totalRelayFee.pct),
      total: BigInt(data.totalRelayFee.total)
    },
    relayerCapitalFee: {
      pct: BigInt(data.relayerCapitalFee.pct),
      total: BigInt(data.relayerCapitalFee.total)
    },
    relayerGasFee: {
      pct: BigInt(data.relayerGasFee.pct),
      total: BigInt(data.relayerGasFee.total)
    },
    lpFee: {
      pct: BigInt(data.lpFee.pct),
      total: BigInt(data.lpFee.total)
    },
    limits: {
      minDeposit: BigInt(data.limits.minDeposit),
      maxDeposit: BigInt(data.limits.maxDeposit),
      maxDepositInstant: BigInt(data.limits.maxDepositInstant)
    }
  };
}

// src/actions/getAvailableRoutes.ts
async function getAvailableRoutes({
  apiUrl = MAINNET_API_URL,
  logger,
  ...params
}) {
  const routes = await fetchAcrossApi(
    `${apiUrl}/available-routes`,
    params,
    logger
  );
  return routes.map((route) => ({
    isNative: route.isNative,
    originChainId: route.originChainId,
    inputToken: route.originToken,
    destinationChainId: route.destinationChainId,
    outputToken: route.destinationToken,
    inputTokenSymbol: route.originTokenSymbol,
    outputTokenSymbol: route.destinationTokenSymbol
  }));
}

// src/actions/getLimits.ts
async function getLimits({
  apiUrl = MAINNET_API_URL,
  logger,
  ...params
}) {
  const limits = await fetchAcrossApi(`${apiUrl}/limits`, params, logger);
  return {
    minDeposit: BigInt(limits.minDeposit),
    maxDeposit: BigInt(limits.maxDeposit),
    maxDepositInstant: BigInt(limits.maxDepositInstant)
  };
}

// src/actions/getQuote.ts
async function getQuote(params) {
  const {
    route,
    recipient: _recipient,
    inputAmount,
    crossChainMessage,
    logger,
    apiUrl
  } = params;
  let message = "0x";
  let recipient = _recipient;
  if (crossChainMessage && typeof crossChainMessage === "object") {
    if (crossChainMessage.actions.length === 0) {
      throw new Error("No 'crossChainMessage.actions' provided");
    }
    logger == null ? void 0 : logger.debug(
      "Building cross chain message for actions:",
      crossChainMessage.actions
    );
    message = buildMulticallHandlerMessage({
      actions: crossChainMessage.actions,
      fallbackRecipient: crossChainMessage.fallbackRecipient
    });
    logger == null ? void 0 : logger.debug("Original message:", message);
    recipient = getMultiCallHandlerAddress(route.destinationChainId);
    logger == null ? void 0 : logger.debug(`Recipient ${recipient}`);
  }
  const { outputAmount, ...fees } = await getSuggestedFees({
    ...route,
    amount: inputAmount,
    recipient,
    message,
    logger,
    apiUrl
  });
  logger == null ? void 0 : logger.debug("fees", fees);
  if (crossChainMessage && typeof crossChainMessage === "object") {
    for (const action of crossChainMessage.actions) {
      if (action.update || action.updateCallData || action.updateValue) {
        let callData;
        let value;
        if (action.update) {
          const updated = action.update(outputAmount);
          callData = updated.callData;
          value = updated.value;
        } else {
          callData = action.updateCallData ? action.updateCallData(outputAmount) : action.callData;
          value = action.updateValue ? action.updateValue(outputAmount) : BigInt(action.value);
        }
        action.callData = callData;
        action.value = value;
        logger == null ? void 0 : logger.debug("Updated calldata:", action.callData);
        logger == null ? void 0 : logger.debug("Updated value:", action.value);
      }
    }
    message = buildMulticallHandlerMessage({
      actions: crossChainMessage.actions,
      fallbackRecipient: crossChainMessage.fallbackRecipient
    });
    logger == null ? void 0 : logger.debug(
      `Updated message with output amount ${outputAmount}`,
      message
    );
  }
  const {
    // partial deposit args
    timestamp,
    exclusiveRelayer,
    exclusivityDeadline,
    spokePoolAddress,
    destinationSpokePoolAddress,
    // limits
    isAmountTooLow,
    limits,
    // fees
    lpFee,
    relayerGasFee,
    relayerCapitalFee,
    totalRelayFee,
    // misc
    estimatedFillTimeSec
  } = fees;
  return {
    deposit: {
      inputAmount: BigInt(inputAmount),
      outputAmount,
      recipient,
      message,
      quoteTimestamp: Number(timestamp),
      exclusiveRelayer,
      exclusivityDeadline,
      spokePoolAddress,
      destinationSpokePoolAddress,
      ...route
    },
    limits,
    fees: {
      lpFee,
      relayerGasFee,
      relayerCapitalFee,
      totalRelayFee
    },
    isAmountTooLow,
    estimatedFillTimeSec
  };
}

// src/actions/simulateDepositTx.ts
import {
  zeroAddress
} from "viem";

// src/abis/SpokePool.ts
var spokePoolAbi = [
  { inputs: [], name: "DepositsArePaused", type: "error" },
  { inputs: [], name: "DisabledRoute", type: "error" },
  { inputs: [], name: "ExpiredFillDeadline", type: "error" },
  { inputs: [], name: "FillsArePaused", type: "error" },
  { inputs: [], name: "InvalidChainId", type: "error" },
  { inputs: [], name: "InvalidDepositorSignature", type: "error" },
  { inputs: [], name: "InvalidExclusiveRelayer", type: "error" },
  { inputs: [], name: "InvalidExclusivityDeadline", type: "error" },
  { inputs: [], name: "InvalidFillDeadline", type: "error" },
  { inputs: [], name: "InvalidQuoteTimestamp", type: "error" },
  { inputs: [], name: "InvalidRelayerFeePct", type: "error" },
  { inputs: [], name: "MaxTransferSizeExceeded", type: "error" },
  { inputs: [], name: "MsgValueDoesNotMatchInputAmount", type: "error" },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "totalFilledAmount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "fillAmount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "repaymentChainId",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint256",
        name: "originChainId",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "destinationChainId",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "int64",
        name: "relayerFeePct",
        type: "int64"
      },
      {
        indexed: false,
        internalType: "int64",
        name: "realizedLpFeePct",
        type: "int64"
      },
      {
        indexed: true,
        internalType: "uint32",
        name: "depositId",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "destinationToken",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "relayer",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "recipient",
        type: "address"
      },
      { indexed: false, internalType: "bytes", name: "message", type: "bytes" },
      {
        components: [
          { internalType: "address", name: "recipient", type: "address" },
          { internalType: "bytes", name: "message", type: "bytes" },
          { internalType: "int64", name: "relayerFeePct", type: "int64" },
          { internalType: "bool", name: "isSlowRelay", type: "bool" },
          {
            internalType: "int256",
            name: "payoutAdjustmentPct",
            type: "int256"
          }
        ],
        indexed: false,
        internalType: "struct SpokePool.RelayExecutionInfo",
        name: "updatableRelayData",
        type: "tuple"
      }
    ],
    name: "FilledRelay",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "inputToken",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "outputToken",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "inputAmount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "outputAmount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "repaymentChainId",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint256",
        name: "originChainId",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint32",
        name: "depositId",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "fillDeadline",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "exclusivityDeadline",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "exclusiveRelayer",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "relayer",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "recipient",
        type: "address"
      },
      { indexed: false, internalType: "bytes", name: "message", type: "bytes" },
      {
        components: [
          {
            internalType: "address",
            name: "updatedRecipient",
            type: "address"
          },
          { internalType: "bytes", name: "updatedMessage", type: "bytes" },
          {
            internalType: "uint256",
            name: "updatedOutputAmount",
            type: "uint256"
          },
          {
            internalType: "enum V3SpokePoolInterface.FillType",
            name: "fillType",
            type: "uint8"
          }
        ],
        indexed: false,
        internalType: "struct V3SpokePoolInterface.V3RelayExecutionEventInfo",
        name: "relayExecutionInfo",
        type: "tuple"
      }
    ],
    name: "FilledV3Relay",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "amount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "originChainId",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint256",
        name: "destinationChainId",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "int64",
        name: "relayerFeePct",
        type: "int64"
      },
      {
        indexed: true,
        internalType: "uint32",
        name: "depositId",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "quoteTimestamp",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "originToken",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "recipient",
        type: "address"
      },
      {
        indexed: true,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      { indexed: false, internalType: "bytes", name: "message", type: "bytes" }
    ],
    name: "FundsDeposited",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "int64",
        name: "newRelayerFeePct",
        type: "int64"
      },
      {
        indexed: true,
        internalType: "uint32",
        name: "depositId",
        type: "uint32"
      },
      {
        indexed: true,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "updatedRecipient",
        type: "address"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "updatedMessage",
        type: "bytes"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "depositorSignature",
        type: "bytes"
      }
    ],
    name: "RequestedSpeedUpDeposit",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "updatedOutputAmount",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint32",
        name: "depositId",
        type: "uint32"
      },
      {
        indexed: true,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "updatedRecipient",
        type: "address"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "updatedMessage",
        type: "bytes"
      },
      {
        indexed: false,
        internalType: "bytes",
        name: "depositorSignature",
        type: "bytes"
      }
    ],
    name: "RequestedSpeedUpV3Deposit",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "inputToken",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "outputToken",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "inputAmount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "outputAmount",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint256",
        name: "originChainId",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint32",
        name: "depositId",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "fillDeadline",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "exclusivityDeadline",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "address",
        name: "exclusiveRelayer",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "recipient",
        type: "address"
      },
      { indexed: false, internalType: "bytes", name: "message", type: "bytes" }
    ],
    name: "RequestedV3SlowFill",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "uint256",
        name: "amountToReturn",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint256",
        name: "chainId",
        type: "uint256"
      },
      { indexed: true, internalType: "uint32", name: "leafId", type: "uint32" },
      {
        indexed: true,
        internalType: "address",
        name: "l2TokenAddress",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "caller",
        type: "address"
      }
    ],
    name: "TokensBridged",
    type: "event"
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: "address",
        name: "inputToken",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "outputToken",
        type: "address"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "inputAmount",
        type: "uint256"
      },
      {
        indexed: false,
        internalType: "uint256",
        name: "outputAmount",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint256",
        name: "destinationChainId",
        type: "uint256"
      },
      {
        indexed: true,
        internalType: "uint32",
        name: "depositId",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "quoteTimestamp",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "fillDeadline",
        type: "uint32"
      },
      {
        indexed: false,
        internalType: "uint32",
        name: "exclusivityDeadline",
        type: "uint32"
      },
      {
        indexed: true,
        internalType: "address",
        name: "depositor",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "recipient",
        type: "address"
      },
      {
        indexed: false,
        internalType: "address",
        name: "exclusiveRelayer",
        type: "address"
      },
      { indexed: false, internalType: "bytes", name: "message", type: "bytes" }
    ],
    name: "V3FundsDeposited",
    type: "event"
  },
  {
    inputs: [],
    name: "EMPTY_RELAYER",
    outputs: [{ internalType: "address", name: "", type: "address" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "EMPTY_REPAYMENT_CHAIN_ID",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "INFINITE_FILL_DEADLINE",
    outputs: [{ internalType: "uint32", name: "", type: "uint32" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "MAX_TRANSFER_SIZE",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "UPDATE_V3_DEPOSIT_DETAILS_HASH",
    outputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "chainId",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "recipient", type: "address" },
      { internalType: "address", name: "originToken", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "uint256", name: "destinationChainId", type: "uint256" },
      { internalType: "int64", name: "relayerFeePct", type: "int64" },
      { internalType: "uint32", name: "quoteTimestamp", type: "uint32" },
      { internalType: "bytes", name: "message", type: "bytes" },
      { internalType: "uint256", name: "", type: "uint256" }
    ],
    name: "deposit",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "depositor", type: "address" },
      { internalType: "address", name: "recipient", type: "address" },
      { internalType: "address", name: "inputToken", type: "address" },
      { internalType: "address", name: "outputToken", type: "address" },
      { internalType: "uint256", name: "inputAmount", type: "uint256" },
      { internalType: "uint256", name: "outputAmount", type: "uint256" },
      { internalType: "uint256", name: "destinationChainId", type: "uint256" },
      { internalType: "address", name: "exclusiveRelayer", type: "address" },
      { internalType: "uint32", name: "quoteTimestamp", type: "uint32" },
      { internalType: "uint32", name: "fillDeadline", type: "uint32" },
      {
        internalType: "uint32",
        name: "exclusivityDeadlineOffset",
        type: "uint32"
      },
      { internalType: "bytes", name: "message", type: "bytes" }
    ],
    name: "depositExclusive",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "depositor", type: "address" },
      { internalType: "address", name: "recipient", type: "address" },
      { internalType: "address", name: "originToken", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" },
      { internalType: "uint256", name: "destinationChainId", type: "uint256" },
      { internalType: "int64", name: "relayerFeePct", type: "int64" },
      { internalType: "uint32", name: "quoteTimestamp", type: "uint32" },
      { internalType: "bytes", name: "message", type: "bytes" },
      { internalType: "uint256", name: "", type: "uint256" }
    ],
    name: "depositFor",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [],
    name: "depositQuoteTimeBuffer",
    outputs: [{ internalType: "uint32", name: "", type: "uint32" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "depositor", type: "address" },
      { internalType: "address", name: "recipient", type: "address" },
      { internalType: "address", name: "inputToken", type: "address" },
      { internalType: "address", name: "outputToken", type: "address" },
      { internalType: "uint256", name: "inputAmount", type: "uint256" },
      { internalType: "uint256", name: "outputAmount", type: "uint256" },
      { internalType: "uint256", name: "destinationChainId", type: "uint256" },
      { internalType: "address", name: "exclusiveRelayer", type: "address" },
      { internalType: "uint32", name: "quoteTimestamp", type: "uint32" },
      { internalType: "uint32", name: "fillDeadline", type: "uint32" },
      { internalType: "uint32", name: "exclusivityDeadline", type: "uint32" },
      { internalType: "bytes", name: "message", type: "bytes" }
    ],
    name: "depositV3",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "depositor", type: "address" },
      { internalType: "address", name: "recipient", type: "address" },
      { internalType: "address", name: "inputToken", type: "address" },
      { internalType: "address", name: "outputToken", type: "address" },
      { internalType: "uint256", name: "inputAmount", type: "uint256" },
      { internalType: "uint256", name: "outputAmount", type: "uint256" },
      { internalType: "uint256", name: "destinationChainId", type: "uint256" },
      { internalType: "address", name: "exclusiveRelayer", type: "address" },
      { internalType: "uint32", name: "fillDeadlineOffset", type: "uint32" },
      { internalType: "uint32", name: "exclusivityDeadline", type: "uint32" },
      { internalType: "bytes", name: "message", type: "bytes" }
    ],
    name: "depositV3Now",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "", type: "address" },
      { internalType: "uint256", name: "", type: "uint256" }
    ],
    name: "enabledDepositRoutes",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [],
    name: "fillDeadlineBuffer",
    outputs: [{ internalType: "uint32", name: "", type: "uint32" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes32", name: "", type: "bytes32" }],
    name: "fillStatuses",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "depositor", type: "address" },
          { internalType: "address", name: "recipient", type: "address" },
          {
            internalType: "address",
            name: "exclusiveRelayer",
            type: "address"
          },
          { internalType: "address", name: "inputToken", type: "address" },
          { internalType: "address", name: "outputToken", type: "address" },
          { internalType: "uint256", name: "inputAmount", type: "uint256" },
          { internalType: "uint256", name: "outputAmount", type: "uint256" },
          { internalType: "uint256", name: "originChainId", type: "uint256" },
          { internalType: "uint32", name: "depositId", type: "uint32" },
          { internalType: "uint32", name: "fillDeadline", type: "uint32" },
          {
            internalType: "uint32",
            name: "exclusivityDeadline",
            type: "uint32"
          },
          { internalType: "bytes", name: "message", type: "bytes" }
        ],
        internalType: "struct V3SpokePoolInterface.V3RelayData",
        name: "relayData",
        type: "tuple"
      },
      { internalType: "uint256", name: "repaymentChainId", type: "uint256" }
    ],
    name: "fillV3Relay",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "depositor", type: "address" },
          { internalType: "address", name: "recipient", type: "address" },
          {
            internalType: "address",
            name: "exclusiveRelayer",
            type: "address"
          },
          { internalType: "address", name: "inputToken", type: "address" },
          { internalType: "address", name: "outputToken", type: "address" },
          { internalType: "uint256", name: "inputAmount", type: "uint256" },
          { internalType: "uint256", name: "outputAmount", type: "uint256" },
          { internalType: "uint256", name: "originChainId", type: "uint256" },
          { internalType: "uint32", name: "depositId", type: "uint32" },
          { internalType: "uint32", name: "fillDeadline", type: "uint32" },
          {
            internalType: "uint32",
            name: "exclusivityDeadline",
            type: "uint32"
          },
          { internalType: "bytes", name: "message", type: "bytes" }
        ],
        internalType: "struct V3SpokePoolInterface.V3RelayData",
        name: "relayData",
        type: "tuple"
      },
      { internalType: "uint256", name: "repaymentChainId", type: "uint256" },
      { internalType: "uint256", name: "updatedOutputAmount", type: "uint256" },
      { internalType: "address", name: "updatedRecipient", type: "address" },
      { internalType: "bytes", name: "updatedMessage", type: "bytes" },
      { internalType: "bytes", name: "depositorSignature", type: "bytes" }
    ],
    name: "fillV3RelayWithUpdatedDeposit",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "getCurrentTime",
    outputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes[]", name: "data", type: "bytes[]" }],
    name: "multicall",
    outputs: [{ internalType: "bytes[]", name: "results", type: "bytes[]" }],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "numberOfDeposits",
    outputs: [{ internalType: "uint32", name: "", type: "uint32" }],
    stateMutability: "view",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "depositor", type: "address" },
          { internalType: "address", name: "recipient", type: "address" },
          {
            internalType: "address",
            name: "exclusiveRelayer",
            type: "address"
          },
          { internalType: "address", name: "inputToken", type: "address" },
          { internalType: "address", name: "outputToken", type: "address" },
          { internalType: "uint256", name: "inputAmount", type: "uint256" },
          { internalType: "uint256", name: "outputAmount", type: "uint256" },
          { internalType: "uint256", name: "originChainId", type: "uint256" },
          { internalType: "uint32", name: "depositId", type: "uint32" },
          { internalType: "uint32", name: "fillDeadline", type: "uint32" },
          {
            internalType: "uint32",
            name: "exclusivityDeadline",
            type: "uint32"
          },
          { internalType: "bytes", name: "message", type: "bytes" }
        ],
        internalType: "struct V3SpokePoolInterface.V3RelayData",
        name: "relayData",
        type: "tuple"
      }
    ],
    name: "requestV3SlowFill",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [
      { internalType: "address", name: "depositor", type: "address" },
      { internalType: "uint32", name: "depositId", type: "uint32" },
      { internalType: "uint256", name: "updatedOutputAmount", type: "uint256" },
      { internalType: "address", name: "updatedRecipient", type: "address" },
      { internalType: "bytes", name: "updatedMessage", type: "bytes" },
      { internalType: "bytes", name: "depositorSignature", type: "bytes" }
    ],
    name: "speedUpV3Deposit",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [{ internalType: "bytes[]", name: "data", type: "bytes[]" }],
    name: "tryMulticall",
    outputs: [
      {
        components: [
          { internalType: "bool", name: "success", type: "bool" },
          { internalType: "bytes", name: "returnData", type: "bytes" }
        ],
        internalType: "struct MultiCallerUpgradeable.Result[]",
        name: "results",
        type: "tuple[]"
      }
    ],
    stateMutability: "nonpayable",
    type: "function"
  },
  {
    inputs: [],
    name: "wrappedNativeToken",
    outputs: [
      { internalType: "contract WETH9Interface", name: "", type: "address" }
    ],
    stateMutability: "view",
    type: "function"
  },
  { stateMutability: "payable", type: "receive" }
];

// src/actions/simulateDepositTx.ts
async function simulateDepositTx(params) {
  const { walletClient, deposit, publicClient, integratorId, logger } = params;
  const {
    originChainId,
    destinationChainId,
    inputToken,
    outputToken,
    inputAmount,
    outputAmount,
    recipient,
    message,
    isNative,
    spokePoolAddress,
    fillDeadline: _fillDeadline,
    exclusiveRelayer,
    exclusivityDeadline,
    quoteTimestamp
  } = deposit;
  const account = walletClient.account;
  if (!account) {
    throw new Error("Wallet account has to be set");
  }
  const connectedChainId = await walletClient.getChainId();
  if (connectedChainId !== originChainId) {
    throw new Error(
      `Connected chainId ${connectedChainId} does not match originChainId ${originChainId}`
    );
  }
  let fillDeadline = _fillDeadline;
  if (!fillDeadline) {
    const [fillDeadlineBufferResult, getCurrentTimeResult] = await publicClient.multicall({
      contracts: [
        {
          address: spokePoolAddress,
          abi: spokePoolAbi,
          functionName: "fillDeadlineBuffer"
        },
        {
          address: spokePoolAddress,
          abi: spokePoolAbi,
          functionName: "getCurrentTime"
        }
      ]
    });
    if (fillDeadlineBufferResult.error || getCurrentTimeResult.error) {
      const error = fillDeadlineBufferResult.error || getCurrentTimeResult.error;
      throw new Error(
        `Failed to fetch 'fillDeadlineBuffer' or 'getCurrentTime': ${error}`
      );
    }
    fillDeadline = Number(getCurrentTimeResult.result) + fillDeadlineBufferResult.result;
  }
  const useExclusiveRelayer = exclusiveRelayer !== zeroAddress && exclusivityDeadline > 0;
  logger == null ? void 0 : logger.debug(`Using exclusive relayer: ${useExclusiveRelayer}`);
  const result = await publicClient.simulateContract({
    account: walletClient.account,
    abi: spokePoolAbi,
    address: spokePoolAddress,
    functionName: useExclusiveRelayer ? "depositExclusive" : "depositV3",
    args: [
      account.address,
      recipient ?? account.address,
      inputToken,
      outputToken,
      BigInt(inputAmount),
      outputAmount,
      BigInt(destinationChainId),
      exclusiveRelayer,
      quoteTimestamp,
      fillDeadline,
      exclusivityDeadline,
      message
    ],
    value: isNative ? BigInt(inputAmount) : 0n,
    dataSuffix: getIntegratorDataSuffix(integratorId)
  });
  logger == null ? void 0 : logger.debug("Simulation result", result);
  return result;
}

// src/actions/getDepositFromLogs.ts
import { parseEventLogs } from "viem";
function getDepositFromLogs({
  originChainId,
  receipt,
  filter
}) {
  const parsedLogs = parseEventLogs({
    abi: spokePoolAbi,
    eventName: "V3FundsDeposited",
    logs: receipt.logs,
    args: filter
  });
  if (parsedLogs.length > 1) {
    throw new Error("Multiple deposit logs found. Specify filter");
  }
  const depositLog = parsedLogs[0];
  if (!depositLog) {
    return void 0;
  }
  return {
    depositId: depositLog.args.depositId,
    depositTxHash: receipt.transactionHash,
    depositTxBlock: receipt.blockNumber,
    originChainId,
    destinationChainId: Number(depositLog.args.destinationChainId),
    inputToken: depositLog.args.inputToken,
    outputToken: depositLog.args.outputToken,
    inputAmount: depositLog.args.inputAmount,
    outputAmount: depositLog.args.outputAmount,
    quoteTimestamp: depositLog.args.quoteTimestamp,
    fillDeadline: depositLog.args.fillDeadline,
    exclusivityDeadline: depositLog.args.exclusivityDeadline,
    depositor: depositLog.args.depositor,
    recipient: depositLog.args.recipient,
    exclusiveRelayer: depositLog.args.exclusiveRelayer,
    message: depositLog.args.message,
    status: "pending"
  };
}

// src/actions/waitForDepositTx.ts
async function waitForDepositTx(params) {
  const { transactionHash, originChainId, publicClient } = params;
  const receipt = await publicClient.waitForTransactionReceipt({
    hash: transactionHash
  });
  const depositLog = getDepositFromLogs({ originChainId, receipt });
  if (!depositLog || !depositLog.depositId) {
    throw new NoDepositLogError(receipt.transactionHash, publicClient.chain.id);
  }
  const depositId = depositLog.depositId;
  return {
    depositTxReceipt: receipt,
    depositId
  };
}

// src/actions/getFillByDepositTx.ts
import {
  parseAbiItem,
  parseEventLogs as parseEventLogs2
} from "viem";
async function getFillByDepositTx(params) {
  const {
    deposit,
    fromBlock,
    destinationChainClient,
    indexerUrl = MAINNET_INDEXER_API,
    logger
  } = params;
  try {
    const data = await fetchIndexerApi(
      `${indexerUrl}/deposit/status`,
      {
        depositId: deposit.depositId,
        originChainId: deposit.originChainId
      }
    );
    if ((data == null ? void 0 : data.status) === "filled" && (data == null ? void 0 : data.fillTx)) {
      const fillTxReceipt2 = await destinationChainClient.getTransactionReceipt({
        hash: data.fillTx
      });
      const fillTxBlock = await destinationChainClient.getBlock({
        blockNumber: fillTxReceipt2.blockNumber
      });
      if (deposit.message !== "0x") {
        const [callsFailedLog] = parseEventLogs2({
          abi: [
            parseAbiItem(
              "event CallsFailed(Call[] calls, address indexed fallbackRecipient)"
            )
          ],
          logs: fillTxReceipt2.logs
        });
        return {
          actionSuccess: !callsFailedLog,
          fillTxReceipt: fillTxReceipt2,
          fillTxTimestamp: fillTxBlock.timestamp
        };
      }
      return {
        fillTxReceipt: fillTxReceipt2,
        fillTxTimestamp: fillTxBlock.timestamp
      };
    }
  } catch (e) {
    logger == null ? void 0 : logger.warn(`Failed to get fill status from indexer: ${e}`);
  }
  const [fillEvent] = await destinationChainClient.getLogs({
    address: deposit.destinationSpokePoolAddress,
    event: {
      anonymous: false,
      inputs: [
        {
          indexed: false,
          internalType: "address",
          name: "inputToken",
          type: "address"
        },
        {
          indexed: false,
          internalType: "address",
          name: "outputToken",
          type: "address"
        },
        {
          indexed: false,
          internalType: "uint256",
          name: "inputAmount",
          type: "uint256"
        },
        {
          indexed: false,
          internalType: "uint256",
          name: "outputAmount",
          type: "uint256"
        },
        {
          indexed: false,
          internalType: "uint256",
          name: "repaymentChainId",
          type: "uint256"
        },
        {
          indexed: true,
          internalType: "uint256",
          name: "originChainId",
          type: "uint256"
        },
        {
          indexed: true,
          internalType: "uint32",
          name: "depositId",
          type: "uint32"
        },
        {
          indexed: false,
          internalType: "uint32",
          name: "fillDeadline",
          type: "uint32"
        },
        {
          indexed: false,
          internalType: "uint32",
          name: "exclusivityDeadline",
          type: "uint32"
        },
        {
          indexed: false,
          internalType: "address",
          name: "exclusiveRelayer",
          type: "address"
        },
        {
          indexed: true,
          internalType: "address",
          name: "relayer",
          type: "address"
        },
        {
          indexed: false,
          internalType: "address",
          name: "depositor",
          type: "address"
        },
        {
          indexed: false,
          internalType: "address",
          name: "recipient",
          type: "address"
        },
        {
          indexed: false,
          internalType: "bytes",
          name: "message",
          type: "bytes"
        },
        {
          components: [
            {
              internalType: "address",
              name: "updatedRecipient",
              type: "address"
            },
            { internalType: "bytes", name: "updatedMessage", type: "bytes" },
            {
              internalType: "uint256",
              name: "updatedOutputAmount",
              type: "uint256"
            },
            {
              internalType: "enum V3SpokePoolInterface.FillType",
              name: "fillType",
              type: "uint8"
            }
          ],
          indexed: false,
          internalType: "struct V3SpokePoolInterface.V3RelayExecutionEventInfo",
          name: "relayExecutionInfo",
          type: "tuple"
        }
      ],
      name: "FilledV3Relay",
      type: "event"
    },
    args: {
      depositId: deposit.depositId,
      originChainId: BigInt(deposit.originChainId)
    },
    fromBlock: fromBlock ?? 0n
  });
  if (!fillEvent) {
    throw new NoFillLogError(
      deposit.depositId,
      deposit.depositTxHash,
      deposit.destinationChainId
    );
  }
  const [fillTxReceipt, fillBlock] = await Promise.all([
    destinationChainClient.getTransactionReceipt({
      hash: fillEvent.transactionHash
    }),
    destinationChainClient.getBlock({
      blockNumber: fillEvent.blockNumber
    })
  ]);
  if (deposit.message !== "0x") {
    const [callsFailedLog] = parseEventLogs2({
      abi: [
        parseAbiItem(
          "event CallsFailed(Call[] calls, address indexed fallbackRecipient)"
        )
      ],
      logs: fillTxReceipt.logs
    });
    return {
      actionSuccess: !callsFailedLog,
      fillTxReceipt,
      fillTxTimestamp: fillBlock.timestamp
    };
  }
  return {
    fillTxReceipt,
    fillTxTimestamp: fillBlock.timestamp
  };
}

// src/actions/getDeposit.ts
import { isHash } from "viem";
async function getDeposit(params) {
  const {
    originChainClient,
    destinationChainClient,
    findBy,
    depositLogFromBlock,
    fillLogFromBlock,
    indexerUrl
  } = params;
  const { originChainId, destinationSpokePoolAddress } = findBy;
  let rawDeposit;
  if (!(findBy.depositTxHash || findBy.depositId && findBy.originSpokePoolAddress)) {
    throw new Error(
      "You must provide either a 'depositTxHash' or 'depositId' + 'spokePoolAddress'"
    );
  }
  if (findBy.depositTxHash) {
    const depositTxHash = findBy.depositTxHash;
    if (!isHash(depositTxHash)) {
      throw new Error(`Can't get receipt for tx hash: ${depositTxHash}`);
    }
    const receipt = await originChainClient.getTransactionReceipt({
      hash: depositTxHash
    });
    if (!receipt) {
      throw new Error(`No receipt found for tx hash: ${depositTxHash}`);
    }
    rawDeposit = getDepositFromLogs({ originChainId, receipt });
  } else if (findBy.depositId && findBy.originSpokePoolAddress) {
    const { originSpokePoolAddress, depositId } = findBy;
    const [depositEvent] = await originChainClient.getLogs({
      address: originSpokePoolAddress,
      event: {
        anonymous: false,
        inputs: [
          {
            indexed: false,
            internalType: "address",
            name: "inputToken",
            type: "address"
          },
          {
            indexed: false,
            internalType: "address",
            name: "outputToken",
            type: "address"
          },
          {
            indexed: false,
            internalType: "uint256",
            name: "inputAmount",
            type: "uint256"
          },
          {
            indexed: false,
            internalType: "uint256",
            name: "outputAmount",
            type: "uint256"
          },
          {
            indexed: true,
            internalType: "uint256",
            name: "destinationChainId",
            type: "uint256"
          },
          {
            indexed: true,
            internalType: "uint32",
            name: "depositId",
            type: "uint32"
          },
          {
            indexed: false,
            internalType: "uint32",
            name: "quoteTimestamp",
            type: "uint32"
          },
          {
            indexed: false,
            internalType: "uint32",
            name: "fillDeadline",
            type: "uint32"
          },
          {
            indexed: false,
            internalType: "uint32",
            name: "exclusivityDeadline",
            type: "uint32"
          },
          {
            indexed: true,
            internalType: "address",
            name: "depositor",
            type: "address"
          },
          {
            indexed: false,
            internalType: "address",
            name: "recipient",
            type: "address"
          },
          {
            indexed: false,
            internalType: "address",
            name: "exclusiveRelayer",
            type: "address"
          },
          {
            indexed: false,
            internalType: "bytes",
            name: "message",
            type: "bytes"
          }
        ],
        name: "V3FundsDeposited",
        type: "event"
      },
      args: {
        depositId
      },
      fromBlock: depositLogFromBlock ?? 0n
    });
    if (!depositEvent) {
      throw new Error(`No deposit found for deposit id: ${depositId}`);
    }
    rawDeposit = {
      depositId,
      originChainId,
      inputToken: depositEvent.args.inputToken,
      outputToken: depositEvent.args.outputToken,
      inputAmount: depositEvent.args.inputAmount,
      outputAmount: depositEvent.args.outputAmount,
      destinationChainId: Number(depositEvent.args.destinationChainId),
      quoteTimestamp: depositEvent.args.quoteTimestamp,
      fillDeadline: depositEvent.args.fillDeadline,
      exclusivityDeadline: depositEvent.args.exclusivityDeadline,
      depositor: depositEvent.args.depositor,
      recipient: depositEvent.args.recipient,
      exclusiveRelayer: depositEvent.args.exclusiveRelayer,
      message: depositEvent.args.message,
      status: "pending",
      depositTxHash: depositEvent.transactionHash,
      depositTxBlock: depositEvent.blockNumber
    };
  }
  if (!rawDeposit) {
    throw new Error(`No deposit found for ${JSON.stringify(findBy)}`);
  }
  try {
    const fill = await getFillByDepositTx({
      deposit: {
        depositId: rawDeposit.depositId,
        originChainId: rawDeposit.originChainId,
        destinationSpokePoolAddress,
        message: rawDeposit.message,
        depositTxHash: rawDeposit.depositTxHash,
        destinationChainId: rawDeposit.destinationChainId
      },
      fromBlock: fillLogFromBlock,
      destinationChainClient,
      indexerUrl
    });
    rawDeposit.fillTxHash = fill.fillTxReceipt.transactionHash;
    rawDeposit.fillTxBlock = fill.fillTxReceipt.blockNumber;
    rawDeposit.status = "filled";
    rawDeposit.actionSuccess = fill.actionSuccess;
  } catch (e) {
    if (e instanceof NoFillLogError) {
    } else {
      throw e;
    }
  }
  return rawDeposit;
}

// src/actions/waitForFillTx.ts
import { parseEventLogs as parseEventLogs3 } from "viem";
async function waitForFillTx(params) {
  const { depositId, destinationChainClient, deposit, fromBlock, logger } = params;
  return new Promise((resolve, reject) => {
    const unwatch = destinationChainClient.watchContractEvent({
      address: deposit.destinationSpokePoolAddress,
      abi: spokePoolAbi,
      eventName: "FilledV3Relay",
      args: {
        depositId,
        originChainId: BigInt(deposit.originChainId)
      },
      fromBlock,
      onError: (error) => {
        console.log("Watch FilledV3Relay event error", error);
      },
      onLogs: async (fillLogs) => {
        if (fillLogs.length) {
          console.log("Fill events found in block", fillLogs);
        }
        const [fillLog] = fillLogs;
        if (fillLog) {
          try {
            const [fillTxReceipt, fillBlock] = await Promise.all([
              destinationChainClient.getTransactionReceipt({
                hash: fillLog.transactionHash
              }),
              destinationChainClient.getBlock({
                blockNumber: fillLog.blockNumber
              })
            ]);
            unwatch();
            if (deposit.message !== "0x") {
              const logs = parseEventLogs3({
                abi: MulticallHandlerAbi,
                logs: fillTxReceipt.logs
              });
              logger == null ? void 0 : logger.debug("Fill Logs", logs);
              const actionSuccess = !logs.some(
                (log) => log.eventName === "CallsFailed"
              );
              resolve({
                actionSuccess,
                fillTxReceipt,
                fillTxTimestamp: fillBlock.timestamp
              });
            }
            resolve({
              fillTxReceipt,
              fillTxTimestamp: fillBlock.timestamp
            });
          } catch (error) {
            unwatch();
            reject(error);
          }
        }
      }
    });
  });
}

// src/actions/simulateApproveTx.ts
import {
  parseAbi
} from "viem";
async function simulateApproveTx(params) {
  const { walletClient, publicClient, spender, approvalAmount, tokenAddress } = params;
  if (!walletClient.account) {
    throw new Error("Wallet account has to be set");
  }
  const simulationResult = await publicClient.simulateContract({
    account: walletClient.account,
    abi: parseAbi([
      "function approve(address spender, uint256 amount) public returns (bool)"
    ]),
    address: tokenAddress,
    functionName: "approve",
    args: [spender, approvalAmount]
  });
  return simulationResult;
}

// src/actions/executeQuote.ts
import {
  parseAbi as parseAbi2
} from "viem";
async function executeQuote(params) {
  const {
    integratorId,
    deposit,
    walletClient,
    originClient,
    destinationClient,
    skipAllowanceCheck,
    infiniteApproval,
    throwOnError = true,
    forceOriginChain,
    onProgress,
    logger
  } = params;
  const onProgressHandler = onProgress || ((progress) => defaultProgressHandler(progress, logger));
  let txRequest;
  let currentTransactionProgress = {
    status: "idle",
    step: "approve"
  };
  let currentProgressMeta;
  try {
    const account = walletClient.account;
    if (!account) {
      throw new Error("Wallet account has to be set");
    }
    if (forceOriginChain) {
      await walletClient.switchChain({
        id: deposit.originChainId
      });
    }
    const connectedChainId = await walletClient.getChainId();
    if (connectedChainId !== deposit.originChainId) {
      throw new Error(
        `Connected chain ${connectedChainId} does not match 'originChainId' ${deposit.originChainId}`
      );
    }
    const { inputToken, inputAmount, spokePoolAddress } = deposit;
    if (!skipAllowanceCheck && !deposit.isNative) {
      const allowance = await originClient.readContract({
        address: inputToken,
        abi: parseAbi2([
          "function allowance(address owner, address spender) public view returns (uint256)"
        ]),
        functionName: "allowance",
        args: [account.address, spokePoolAddress]
      });
      logger == null ? void 0 : logger.debug("Allowance", {
        allowance,
        owner: account.address,
        spender: spokePoolAddress,
        inputToken
      });
      if (BigInt(inputAmount) > allowance) {
        const approvalAmount = infiniteApproval ? BigInt(4294967295) : BigInt(inputAmount);
        currentProgressMeta = {
          approvalAmount,
          spender: spokePoolAddress
        };
        currentTransactionProgress = {
          step: "approve",
          status: "simulationPending",
          meta: currentProgressMeta
        };
        onProgressHandler(currentTransactionProgress);
        const { request } = await simulateApproveTx({
          walletClient,
          publicClient: originClient,
          spender: spokePoolAddress,
          approvalAmount,
          tokenAddress: inputToken
        });
        txRequest = request;
        currentTransactionProgress = {
          ...currentTransactionProgress,
          status: "simulationSuccess",
          txRequest: request
        };
        onProgressHandler(currentTransactionProgress);
        const approveTxHash = await walletClient.writeContract({
          account,
          ...txRequest
        });
        currentTransactionProgress = {
          ...currentTransactionProgress,
          status: "txPending",
          txHash: approveTxHash
        };
        onProgressHandler(currentTransactionProgress);
        const approveTxReceipt = await originClient.waitForTransactionReceipt({
          hash: approveTxHash
        });
        currentTransactionProgress = {
          ...currentTransactionProgress,
          status: "txSuccess",
          txReceipt: approveTxReceipt
        };
        onProgressHandler(currentTransactionProgress);
      }
    }
    currentProgressMeta = {
      deposit
    };
    currentTransactionProgress = {
      step: "deposit",
      status: "simulationPending",
      meta: currentProgressMeta
    };
    onProgressHandler(currentTransactionProgress);
    const { request: _request } = await simulateDepositTx({
      walletClient,
      publicClient: originClient,
      deposit,
      integratorId,
      logger
    });
    txRequest = _request;
    currentTransactionProgress = {
      ...currentTransactionProgress,
      status: "simulationSuccess",
      txRequest: _request
    };
    onProgressHandler(currentTransactionProgress);
    const depositTxHash = await walletClient.writeContract({
      account,
      ...txRequest
    });
    currentTransactionProgress = {
      ...currentTransactionProgress,
      status: "txPending",
      txHash: depositTxHash
    };
    onProgressHandler(currentTransactionProgress);
    const destinationBlock = await destinationClient.getBlockNumber();
    const { depositId, depositTxReceipt } = await waitForDepositTx({
      originChainId: deposit.originChainId,
      transactionHash: depositTxHash,
      publicClient: originClient
    });
    currentTransactionProgress = {
      ...currentTransactionProgress,
      status: "txSuccess",
      txReceipt: depositTxReceipt,
      depositId
    };
    onProgressHandler(currentTransactionProgress);
    currentProgressMeta = {
      depositId,
      deposit
    };
    currentTransactionProgress = {
      step: "fill",
      status: "txPending",
      meta: currentProgressMeta
    };
    onProgressHandler(currentTransactionProgress);
    const { fillTxReceipt, fillTxTimestamp, actionSuccess } = await waitForFillTx({
      deposit,
      depositId,
      destinationChainClient: destinationClient,
      fromBlock: destinationBlock - 100n
      // TODO: use dynamic block buffer based chain
    });
    currentTransactionProgress = {
      ...currentTransactionProgress,
      status: "txSuccess",
      txReceipt: fillTxReceipt,
      fillTxTimestamp,
      actionSuccess
    };
    onProgressHandler(currentTransactionProgress);
    return { depositId, depositTxReceipt, fillTxReceipt };
  } catch (error) {
    const errorStatus = currentTransactionProgress.status === "txPending" ? "txError" : currentTransactionProgress.status === "simulationPending" ? "simulationError" : "error";
    onProgressHandler({
      ...currentTransactionProgress,
      status: errorStatus,
      error,
      meta: currentProgressMeta
    });
    if (!throwOnError) {
      return { error };
    }
    throw error;
  }
}
function defaultProgressHandler(progress, logger) {
  if (!logger) {
    return;
  }
  logger.debug("Progress", progress);
}

// src/client.ts
var CLIENT_DEFAULTS = {
  pollingInterval: 3e3,
  integratorId: "0xdead",
  logLevel: "ERROR"
};
var AcrossClient = class _AcrossClient {
  static instance = null;
  integratorId;
  publicClients;
  chainInfo;
  walletClient;
  apiUrl;
  indexerUrl;
  logger;
  // Tenderly related options
  tenderly;
  get isTenderlyEnabled() {
    var _a, _b, _c;
    return Boolean(
      ((_a = this.tenderly) == null ? void 0 : _a.accessKey) && ((_b = this.tenderly) == null ? void 0 : _b.accountSlug) && ((_c = this.tenderly) == null ? void 0 : _c.projectSlug)
    );
  }
  constructor(args) {
    var _a;
    const integratorId = (args == null ? void 0 : args.integratorId) ?? CLIENT_DEFAULTS.integratorId;
    assertValidIntegratorId(integratorId);
    this.integratorId = integratorId;
    this.walletClient = args == null ? void 0 : args.walletClient;
    this.publicClients = configurePublicClients(
      args.chains,
      args.pollingInterval ?? CLIENT_DEFAULTS.pollingInterval,
      args == null ? void 0 : args.rpcUrls
    );
    this.indexerUrl = (args == null ? void 0 : args.useTestnet) === true ? TESTNET_INDEXER_API : MAINNET_INDEXER_API;
    this.apiUrl = (args == null ? void 0 : args.useTestnet) === true ? TESTNET_API_URL : MAINNET_API_URL;
    this.logger = (args == null ? void 0 : args.logger) ?? new DefaultLogger((args == null ? void 0 : args.logLevel) ?? CLIENT_DEFAULTS.logLevel);
    this.tenderly = args.tenderly;
    if (this.tenderly) {
      this.tenderly.simOnError = ((_a = args.tenderly) == null ? void 0 : _a.simOnError) ?? true;
    }
    this.logger.debug("Client created with args: \n", args);
  }
  /**
   * Create a new `AcrossClient` instance as a singleton.
   * @param options - See {@link AcrossClientOptions}.
   * @returns A new `AcrossClient` instance if it doesn't exist, otherwise the existing
   * instance.
   */
  static create(options) {
    if (this.instance === null) {
      this.instance = new _AcrossClient(options);
    }
    return this.instance;
  }
  /**
   * Get the existing `AcrossClient` singleton instance.
   * @returns The existing `AcrossClient` instance.
   * @throws If the instance is not initialized.
   */
  static getInstance() {
    if (this.instance === null) {
      throw new Error(
        "AcrossClient has not been initialized. Call create() first."
      );
    }
    return this.instance;
  }
  update(params) {
    this.walletClient = params.walletClient;
  }
  /**
   * @internal
   */
  getPublicClient(chainId) {
    const client = this.publicClients.get(chainId);
    if (!client) {
      throw new ConfigError(`SDK not configured for chain with id ${chainId}.`);
    }
    this.logger.debug(`Using configured public client for chain ${chainId}.`);
    return client;
  }
  async getSpokePoolAddress(chainId) {
    const chainInfo = await this.getChainInfo(chainId);
    return chainInfo.spokePool;
  }
  /**
   * @param chainId - number
   * @returns See {@link AcrossChain}.
   */
  async getChainInfo(chainId) {
    if (!this.chainInfo) {
      const acrossChains = await this.getSupportedChains({
        chainId: Array.from(this.publicClients.keys())
      });
      this.chainInfo = new Map(
        acrossChains.map((acrossChain) => [acrossChain.chainId, acrossChain])
      );
    }
    if (!this.chainInfo.has(chainId)) {
      throw new Error(`Could not find chainInfo for chain with id ${chainId}`);
    }
    return this.chainInfo.get(chainId);
  }
  /**
   * Execute a quote by:
   * 1. Approving the SpokePool contract if necessary
   * 2. Depositing the input token on the origin chain
   * 3. Waiting for the deposit to be filled on the destination chain
   *
   * See {@link executeQuote} for more details.
   *
   * @example
   * ```ts
   * const quote = await client.getQuote({ route, inputAmount });
   * const { depositId } = await client.executeQuote({ deposit: quote.deposit });
   * ```
   *
   * @param params - See {@link ExecuteQuoteParams}.
   * @returns The deposit ID and receipts for the deposit and fill transactions.
   */
  async executeQuote(params) {
    var _a;
    const _walletClient = (params == null ? void 0 : params.walletClient) ?? (this == null ? void 0 : this.walletClient);
    if (!_walletClient) {
      throw new ConfigError(
        "WalletClient needs to be set to call 'executeQuote'"
      );
    }
    try {
      await executeQuote({
        ...params,
        integratorId: this.integratorId,
        logger: this.logger,
        walletClient: _walletClient,
        originClient: this.getPublicClient(params.deposit.originChainId),
        destinationClient: this.getPublicClient(
          params.deposit.destinationChainId
        )
      });
    } catch (e) {
      if (!this.isTenderlyEnabled || !((_a = this.tenderly) == null ? void 0 : _a.simOnError) || !(e instanceof AcrossApiSimulationError || e instanceof ContractFunctionExecutionError)) {
        throw e;
      }
      const isFillSimulationError = e instanceof AcrossApiSimulationError;
      const simParams = isFillSimulationError ? {
        networkId: params.deposit.destinationChainId.toString(),
        from: e.transaction.from,
        to: e.transaction.to,
        data: e.transaction.data
      } : {
        networkId: params.deposit.originChainId.toString(),
        from: e.sender,
        to: e.contractAddress,
        data: encodeFunctionData({
          abi: e.abi,
          functionName: e.functionName,
          args: e.args
        })
      };
      const { simulationId, simulationUrl } = await this.simulateTxOnTenderly({
        value: params.deposit.isNative ? String(params.deposit.inputAmount) : "0",
        ...simParams
      });
      const reason = isFillSimulationError ? e.message : e.shortMessage;
      throw new SimulationError({
        simulationId,
        simulationUrl,
        message: `simulation failed while executing quote: ${reason}`
      });
    }
  }
  /**
   * Get the available routes for a given set of parameters. See {@link getAvailableRoutes}.
   * @param params - See {@link GetAvailableRoutesParams}.
   * @returns See {@link GetAvailableRoutesReturnType}.
   */
  async getAvailableRoutes(params) {
    return getAvailableRoutes({
      ...params,
      apiUrl: this.apiUrl,
      logger: this.logger
    });
  }
  /**
   * Get the suggested fees for a given route. See {@link getSuggestedFees}.
   * @param params - See {@link GetSuggestedFeesParams}.
   * @returns See {@link GetSuggestedFeesReturnType}.
   */
  async getSuggestedFees(params) {
    var _a;
    try {
      const fees = await getSuggestedFees({
        ...params,
        apiUrl: this.apiUrl,
        logger: this.logger
      });
      return fees;
    } catch (e) {
      if (!this.isTenderlyEnabled || !((_a = this.tenderly) == null ? void 0 : _a.simOnError) || !(e instanceof AcrossApiSimulationError)) {
        throw e;
      }
      const { simulationId, simulationUrl } = await this.simulateTxOnTenderly({
        networkId: params.destinationChainId.toString(),
        to: e.transaction.to,
        data: e.transaction.data,
        from: e.transaction.from,
        value: e.transaction.value ?? "0"
      });
      throw new SimulationError({
        simulationId,
        simulationUrl,
        message: `simulation failed while fetching suggested fees: ${e.message}`
      });
    }
  }
  /**
   * Get the deposit limits for a given route. See {@link getLimits}.
   * @param params - See {@link GetLimitsParams}.
   * @returns See {@link GetLimitsReturnType}.
   */
  async getLimits(params) {
    var _a;
    try {
      const limits = await getLimits({
        ...params,
        apiUrl: this.apiUrl,
        logger: this.logger
      });
      return limits;
    } catch (e) {
      if (!((_a = this.tenderly) == null ? void 0 : _a.simOnError) || !(e instanceof AcrossApiSimulationError)) {
        throw e;
      }
      const { simulationId, simulationUrl } = await this.simulateTxOnTenderly({
        networkId: params.destinationChainId.toString(),
        to: e.transaction.to,
        data: e.transaction.data,
        from: e.transaction.from,
        value: e.transaction.value ?? "0"
      });
      throw new SimulationError({
        simulationId,
        simulationUrl,
        message: `simulation failed while fetching limits: ${e.message}`
      });
    }
  }
  /**
   * Get a quote for a given set of parameters. See {@link getQuote}.
   * @param params - See {@link GetQuoteParams}.
   * @returns See {@link Quote}.
   */
  async getQuote(params) {
    var _a;
    try {
      const quote = await getQuote({
        ...params,
        logger: this.logger,
        apiUrl: this.apiUrl
      });
      return quote;
    } catch (e) {
      if (!((_a = this.tenderly) == null ? void 0 : _a.simOnError) || !(e instanceof AcrossApiSimulationError)) {
        throw e;
      }
      const { simulationId, simulationUrl } = await this.simulateTxOnTenderly({
        networkId: params.route.originChainId.toString(),
        to: e.transaction.to,
        data: e.transaction.data,
        from: e.transaction.from,
        value: e.transaction.value ?? "0"
      });
      throw new SimulationError({
        simulationId,
        simulationUrl,
        message: `simulation failed while fetching quote: ${e.message}`
      });
    }
  }
  async simulateDepositTx(params) {
    var _a;
    try {
      const result = await simulateDepositTx({
        ...params,
        integratorId: this.integratorId,
        publicClient: this.getPublicClient(params.deposit.originChainId),
        logger: this.logger
      });
      return result;
    } catch (e) {
      if (!((_a = this.tenderly) == null ? void 0 : _a.simOnError) || !this.isTenderlyEnabled || !(e instanceof ContractFunctionExecutionError)) {
        throw e;
      }
      const { simulationId, simulationUrl } = await this.simulateTxOnTenderly({
        networkId: params.deposit.originChainId.toString(),
        from: e.sender,
        to: e.contractAddress,
        data: encodeFunctionData({
          abi: e.abi,
          functionName: e.functionName,
          args: e.args
        }),
        value: params.deposit.isNative ? String(params.deposit.inputAmount) : "0"
      });
      throw new SimulationError({
        simulationId,
        simulationUrl,
        message: `deposit simulation failed: ${e.shortMessage}`
      });
    }
  }
  async waitForDepositTx(params) {
    return waitForDepositTx({
      ...params,
      publicClient: this.getPublicClient(params.originChainId)
    });
  }
  /**
   * Get a fill after a deposit has been made. See {@link getFillByDepositTx}.
   * @param params - See {@link GetFillByDepositTxParams}.
   * @returns See {@link FillStatus}.
   */
  async getFillByDepositTx(params) {
    return getFillByDepositTx({
      ...params,
      destinationChainClient: this.getPublicClient(
        params.deposit.destinationChainId
      ),
      indexerUrl: this.indexerUrl,
      logger: this.logger
    });
  }
  async waitForFillTx(params) {
    return waitForFillTx({
      ...params,
      destinationChainClient: this.getPublicClient(
        params.deposit.destinationChainId
      ),
      logger: this.logger
    });
  }
  /**
   * Get a deposit by its deposit tx hash or deposit id + spoke pool address. See {@link getDeposit}.
   * @param params - See {@link GetDepositParams}.
   * @returns See {@link Deposit}.
   */
  async getDeposit(params) {
    const originSpokePoolAddress = params.findBy.originSpokePoolAddress ?? await this.getSpokePoolAddress(params.findBy.originChainId);
    const destinationSpokePoolAddress = params.findBy.destinationSpokePoolAddress ?? await this.getSpokePoolAddress(params.findBy.destinationChainId);
    return getDeposit({
      ...params,
      findBy: {
        ...params.findBy,
        originSpokePoolAddress,
        destinationSpokePoolAddress
      },
      indexerUrl: this.indexerUrl,
      originChainClient: this.getPublicClient(params.findBy.originChainId),
      destinationChainClient: this.getPublicClient(
        params.findBy.destinationChainId
      )
    });
  }
  /* -------------------------------- Utilities ------------------------------- */
  async getSupportedChains(params) {
    return getSupportedChains({
      ...params,
      logger: this.logger,
      apiUrl: this.apiUrl
    });
  }
  async simulateTxOnTenderly(params) {
    var _a, _b, _c, _d, _e, _f;
    if (!((_a = this.tenderly) == null ? void 0 : _a.accessKey) || !((_b = this.tenderly) == null ? void 0 : _b.accountSlug) || !((_c = this.tenderly) == null ? void 0 : _c.projectSlug)) {
      throw new ConfigError(
        "Tenderly credentials not set. Client needs to be configured with 'tenderly.accessKey', 'tenderly.accountSlug', and 'tenderly.projectSlug'."
      );
    }
    return simulateTxOnTenderly({
      ...params,
      accessKey: (_d = this.tenderly) == null ? void 0 : _d.accessKey,
      accountSlug: (_e = this.tenderly) == null ? void 0 : _e.accountSlug,
      projectSlug: (_f = this.tenderly) == null ? void 0 : _f.projectSlug,
      enableShare: true
    });
  }
};
function getAcrossClient() {
  return AcrossClient.getInstance();
}
function createAcrossClient(options) {
  return AcrossClient.create(options);
}
export {
  AcrossApiError,
  AcrossApiSimulationError,
  AcrossClient,
  ConfigError,
  DOMAIN_CALLDATA_DELIMITER,
  DefaultLogger,
  DepositRevert,
  HttpError,
  IndexerError,
  LogLevels,
  MulticallHandlerAbi,
  NoDepositLogError,
  NoFillLogError,
  SimulationError,
  assertValidIntegratorId,
  buildMulticallHandlerMessage,
  buildSearchParams,
  configurePublicClients,
  createAcrossClient,
  executeQuote,
  fetchAcrossApi,
  fetchIndexerApi,
  getAcrossClient,
  getAvailableRoutes,
  getCurrentTimeSeconds,
  getDeposit,
  getDepositFromLogs,
  getFillByDepositTx,
  getIntegratorDataSuffix,
  getLimits,
  getMultiCallHandlerAddress,
  getQuote,
  getSuggestedFees,
  getSupportedChains,
  isOk,
  isValidIntegratorId,
  simulateApproveTx,
  simulateDepositTx,
  simulateTxOnTenderly,
  tagIntegratorId,
  waitForDepositTx,
  waitForFillTx
};
